<template>
  <section v-if="total > 0" class="section is-paddingless">
    <div  class="control has-icons-left mb-2 is-pulled-right break-out-up half-width">
      <input  v-model="filterTerm"
              :placeholder="$tc('filter', 1)"
              type="text"
              class="input"
              @input="updateFilterTerm">
      <span class="icon is-left fas fa-search" />
    </div>
    <div style="clear:both" />
    <section>
      <virtual-list :data-component="$options.VirtualScrollCards"
                    :data-sources="related"
                    :extra-props="{ isWidget, openInNewTab: !isIframe }"
                    class="vs-card-list"
                    data-key="id"
                    @tobottom="getMoreRelated">
        <template #footer>
          <utility-block v-show="isLoading"/>
        </template>
      </virtual-list>
    </section>
  </section>
  <section v-else-if="!total" class="no-data py-4">
    <h1 data-int="related-no-data" class="subtitle has-text-centered">
      {{ $t('assetsNotFound', {type: $t('related')}) }}
    </h1>
  </section>
</template>

<script>
import { computed, toRaw, ref } from 'vue';
import { mapState, mapActions, useStore } from 'vuex';
import debounce from '@/plugins/debounce';
import VirtualList from 'vue3-virtual-scroll-list';

import VirtualScrollCards from '@/components/common/VirtualScrollCards.vue'
import Pagination from '@/components/common/Pagination';
import { WidgetTypes } from '@/interfaces/global';
import { useInfoLandingPage } from "@/components/widgets/composables/useInfoLandingPage";
import { useBreakpoints } from '@/helpers/responsiveBreakpoints'

export default {
  name: 'Related',
  VirtualScrollCards,
  mixins: [debounce],
  provide() {
    return {
      eventHandlers: {
        select: this.select,
        navToWidgetContent: this.navToWidgetContent
      }
    }
  },
  setup() {
    const store = useStore()
    const { navigateToRelatedContent } = useInfoLandingPage()
    const { outerWidth } = useBreakpoints()

    const filterTerm = ref('')

    const isIframe = window.top !== window.self
    const isWidget = computed(() => store.getters['widgets/isWidget'])
    const widgetType = computed(() => store.getters['widgets/widgetType'])
    const isLibraryWidget = computed(() => isWidget.value
      && widgetType.value === WidgetTypes.library)
    const isProductPageWidget = computed(() => isWidget.value
      && widgetType.value === WidgetTypes.productPage)

    async function navToWidgetContent(statefulDto) {
      const dto = toRaw(statefulDto)
      const isRelatedMedia = dto.type === "media"
      const isFile = Object.hasOwn(dto, 'contentUrl')
        && Object.hasOwn(dto, 'name')
        && !Object.hasOwn(dto, 'type')
      if ((isLibraryWidget.value || isProductPageWidget.value) && isRelatedMedia) {
        navigateToRelatedContent(dto)
      }
      else if (isFile) {
        const fileData = await fetch(dto.contentUrl)
        const blob = await fileData.blob()

        window.parent.postMessage(
          { item: { blob, name: dto.name },
            action: "downloadFile" },
          store.getters['widgets/targetUri'])
      }
    }

    const isMobile = computed(() => {
      return outerWidth.value <= 768
    })

    const related = computed(() => {
      if (isMobile.value) {
        return store.getters['content/relatedAsMatrixMobile']
      }
      return store.getters['content/relatedAsMatrix']
    })

    return {
      filterTerm, related, isWidget, isIframe,
      navToWidgetContent
    }
  },
  components: {
    VirtualList,
    VirtualScrollCards,
    Pagination
  },
  computed: {
    ...mapState({
      contentLoaded: ({ content }) => content.isLoaded,
      total: ({ content }) => content.related.total,
      offset: ({ content }) => content.related.offset,
      limit: ({ content }) => content.related.limit,
      isLoading: ({ content }) => content.related.loading
    }),
  },
  watch: {
    async contentLoaded() {
      await this.getRelated();
    },
    offset() {
      this.scrollToTop();
    }
  },
  debounceMethods: {
    updateFilterTerm: 500
  },
  methods: {
    ...mapActions({
      getRelated: 'content/getRelated',
      navigateToContent: 'content/navigateToContent',
      getMoreRelated: 'content/getMoreRelated',
      resetOffset: 'content/resetRelatedOffset',
      clearRelated: 'content/clearRelated'
    }),
    scrollToTop() {
      const results = document.getElementsByClassName('related-results');
      if (results.length) {
        results[0].scrollTo(0, 0);
      }
    },
    select(content) {
      this.navigateToContent({ content })
    },
    async updateFilterTerm(e) {
      this.resetOffset();

      const { value } = e.target;
      this.filterTerm = value;

      await this.getRelated(value);
    }
  },
  async created() {
    await this.getRelated();
  },
  beforeUnmount() {
    this.clearRelated(false);
  }
};
</script>

<style scoped lang="scss">
.break-out-up {
  @media screen and (min-width: 769px) {
    margin-top: -3rem;
}}

.related-results {
  flex: 1;
  overflow: auto;
  padding: 1rem;

  .column {
    @media screen and (max-width: 768px) {
      min-width: 8em;
    }

  }
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
